import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Calculator } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import ManufacturingService from '@/api/manufacturing.service';

interface RecipeFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

const RecipeForm: React.FC<RecipeFormProps> = ({ initialData, onSubmit, isLoading = false }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    category: '',
    version: '1.0',
    outputQuantity: 1,
    outputUnit: '',
    estimatedTime: 0,
    difficulty: 'medium',
    instructions: '',
    bomItems: [{ itemId: '', itemName: '', quantity: 1, unit: '', cost: 0, notes: '' }],
    qualityChecks: [{ parameter: '', specification: '', tolerance: '' }]
  });
  
  const [items, setItems] = useState<any[]>([]);
  const [totalCost, setTotalCost] = useState(0);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    fetchItems();
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        code: initialData.code || '',
        description: initialData.description || '',
        category: initialData.category || '',
        version: initialData.version || '1.0',
        outputQuantity: initialData.outputQuantity || 1,
        outputUnit: initialData.outputUnit || '',
        estimatedTime: initialData.estimatedTime || 0,
        difficulty: initialData.difficulty || 'medium',
        instructions: initialData.instructions || '',
        bomItems: initialData.bomItems || [{ itemId: '', itemName: '', quantity: 1, unit: '', cost: 0, notes: '' }],
        qualityChecks: initialData.qualityChecks || [{ parameter: '', specification: '', tolerance: '' }]
      });
    }
  }, [initialData]);

  useEffect(() => {
    calculateTotalCost();
  }, [formData.bomItems]);

  const fetchItems = async () => {
    try {
      const response = await ManufacturingService.getInventoryItems(businessId);
      if (response.data?.success) {
        setItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const calculateTotalCost = () => {
    const total = formData.bomItems.reduce((sum, item) => {
      return sum + (item.quantity * item.cost);
    }, 0);
    setTotalCost(total);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code || formData.bomItems.length === 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields and add at least one BOM item",
      });
      return;
    }

    const validBomItems = formData.bomItems.filter(item => item.itemId && item.quantity > 0);
    if (validBomItems.length === 0) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please add at least one valid BOM item",
      });
      return;
    }

    onSubmit({
      ...formData,
      bomItems: validBomItems,
      totalCost: totalCost
    });
  };

  const addBomItem = () => {
    setFormData(prev => ({
      ...prev,
      bomItems: [...prev.bomItems, { itemId: '', itemName: '', quantity: 1, unit: '', cost: 0, notes: '' }]
    }));
  };

  const removeBomItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      bomItems: prev.bomItems.filter((_, i) => i !== index)
    }));
  };

  const updateBomItem = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      bomItems: prev.bomItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-populate item details when item is selected
          if (field === 'itemId' && value) {
            const selectedItem = items.find(item => item.id.toString() === value);
            if (selectedItem) {
              updatedItem.itemName = selectedItem.name;
              updatedItem.unit = selectedItem.unit;
              updatedItem.cost = selectedItem.cost || 0;
            }
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const addQualityCheck = () => {
    setFormData(prev => ({
      ...prev,
      qualityChecks: [...prev.qualityChecks, { parameter: '', specification: '', tolerance: '' }]
    }));
  };

  const removeQualityCheck = (index: number) => {
    setFormData(prev => ({
      ...prev,
      qualityChecks: prev.qualityChecks.filter((_, i) => i !== index)
    }));
  };

  const updateQualityCheck = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      qualityChecks: prev.qualityChecks.map((check, i) => 
        i === index ? { ...check, [field]: value } : check
      )
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Recipe Information</CardTitle>
          <CardDescription>Basic details about the recipe</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Recipe Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter recipe name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Recipe Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                placeholder="Enter recipe code"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the recipe"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="finished_goods">Finished Goods</SelectItem>
                  <SelectItem value="semi_finished">Semi-Finished</SelectItem>
                  <SelectItem value="raw_materials">Raw Materials</SelectItem>
                  <SelectItem value="packaging">Packaging</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                placeholder="1.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="outputQuantity">Output Quantity</Label>
              <Input
                id="outputQuantity"
                type="number"
                value={formData.outputQuantity}
                onChange={(e) => setFormData(prev => ({ ...prev, outputQuantity: parseFloat(e.target.value) || 1 }))}
                min="0.01"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="outputUnit">Output Unit</Label>
              <Input
                id="outputUnit"
                value={formData.outputUnit}
                onChange={(e) => setFormData(prev => ({ ...prev, outputUnit: e.target.value }))}
                placeholder="kg, pcs, liters"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estimatedTime">Estimated Time (minutes)</Label>
              <Input
                id="estimatedTime"
                type="number"
                value={formData.estimatedTime}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedTime: parseInt(e.target.value) || 0 }))}
                min="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="difficulty">Difficulty Level</Label>
              <Select value={formData.difficulty} onValueChange={(value) => setFormData(prev => ({ ...prev, difficulty: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bill of Materials */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Bill of Materials (BOM)</CardTitle>
              <CardDescription>Materials required for this recipe</CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-lg font-semibold">{formatCurrency(totalCost)}</p>
              </div>
              <Button type="button" onClick={addBomItem} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Unit Cost</TableHead>
                <TableHead>Total Cost</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.bomItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Select 
                      value={item.itemId} 
                      onValueChange={(value) => updateBomItem(index, 'itemId', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((inventoryItem) => (
                          <SelectItem key={inventoryItem.id} value={inventoryItem.id.toString()}>
                            {inventoryItem.name} ({inventoryItem.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateBomItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-20"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={item.unit}
                      onChange={(e) => updateBomItem(index, 'unit', e.target.value)}
                      className="w-16"
                      readOnly
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={item.cost}
                      onChange={(e) => updateBomItem(index, 'cost', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {formatCurrency(item.quantity * item.cost)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={item.notes}
                      onChange={(e) => updateBomItem(index, 'notes', e.target.value)}
                      placeholder="Optional notes"
                      className="w-32"
                    />
                  </TableCell>
                  <TableCell>
                    {formData.bomItems.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBomItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Manufacturing Instructions</CardTitle>
          <CardDescription>Step-by-step instructions for manufacturing</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.instructions}
            onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
            placeholder="Enter detailed manufacturing instructions..."
            rows={6}
          />
        </CardContent>
      </Card>

      {/* Quality Checks */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Quality Control Checks</CardTitle>
              <CardDescription>Quality parameters to check during manufacturing</CardDescription>
            </div>
            <Button type="button" onClick={addQualityCheck} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Check
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parameter</TableHead>
                <TableHead>Specification</TableHead>
                <TableHead>Tolerance</TableHead>
                <TableHead className="w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.qualityChecks.map((check, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={check.parameter}
                      onChange={(e) => updateQualityCheck(index, 'parameter', e.target.value)}
                      placeholder="e.g., Weight, Temperature"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={check.specification}
                      onChange={(e) => updateQualityCheck(index, 'specification', e.target.value)}
                      placeholder="e.g., 100g, 80°C"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={check.tolerance}
                      onChange={(e) => updateQualityCheck(index, 'tolerance', e.target.value)}
                      placeholder="e.g., ±5g, ±2°C"
                    />
                  </TableCell>
                  <TableCell>
                    {formData.qualityChecks.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQualityCheck(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Cost Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded">
              <p className="text-sm text-muted-foreground">Total Material Cost</p>
              <p className="text-2xl font-bold">{formatCurrency(totalCost)}</p>
            </div>
            <div className="text-center p-4 border rounded">
              <p className="text-sm text-muted-foreground">Cost per Unit</p>
              <p className="text-2xl font-bold">
                {formatCurrency(formData.outputQuantity > 0 ? totalCost / formData.outputQuantity : 0)}
              </p>
            </div>
            <div className="text-center p-4 border rounded">
              <p className="text-sm text-muted-foreground">Estimated Time</p>
              <p className="text-2xl font-bold">{formData.estimatedTime} min</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-4">
        <Button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
          {initialData ? 'Update Recipe' : 'Create Recipe'}
        </Button>
      </div>
    </form>
  );
};

export default RecipeForm; 